/**
 * Shape of the `documents` i18n namespace. English is the reference resource —
 * `fr.ts` and `nl.ts` are typed against this interface, so a key added here
 * fails the build until every language supplies it (and `i18n/parity.test.ts`
 * asserts the same at runtime, where a host might load a resource object that
 * never went through tsc).
 *
 * Accounting wording follows `docs/ACCOUNTING-TERMINOLOGY-GLOSSARY.md`; where a
 * string already exists in the app's `common` namespace
 * (`apps/web/public/locales/<lng>/common.json`) the FR/NL translations are taken
 * from there verbatim, so a document and the screen say the same thing.
 */
export const en = {
    common: {
        generatedOn: 'Generated on {{date}}',
        page: 'Page {{page}} of {{total}}',
        status: 'Status',
        project: 'Project',
    },
    identity: {
        iban: 'IBAN',
        bic: 'BIC',
        tradingName: 'Trading Name',
        relatedParty: 'Related Party',
        registrationNumber: {
            NL: 'KvK Number',
            BE: 'BCE/KBO Number',
            DE: 'Handelsregister',
            FR: 'SIREN',
            GB: 'Company Number',
            US: 'State Registration',
            DEFAULT: 'Registration Number',
        },
        vatNumber: {
            NL: 'BTW Number',
            BE: 'BTW Number',
            DE: 'USt-IdNr',
            FR: 'TVA Number',
            GB: 'VAT Number',
            US: 'Sales Tax ID',
            DEFAULT: 'VAT Number',
        },
        taxEntityNumber: {
            NL: 'RSIN',
            US: 'EIN',
            DEFAULT: 'Tax Entity Number',
        },
        taxExempt: {
            NL: 'ANBI Status',
            GB: 'Charity Status',
            US: '501(c)(3) Status',
            DEFAULT: 'Tax Exempt',
        },
    },
    invoice: {
        titleAr: 'Invoice',
        titleArDraft: 'Draft invoice',
        draftNotice: 'This is not an invoice. It gives no right to deduct VAT.',
        cancelledNotice: 'Cancelled — this invoice has been reversed and nothing on it is payable. This is a record of the cancelled invoice, not a credit note.',
        titleAp: 'Bill',
        from: 'From',
        billTo: 'Bill To',
        billFrom: 'Bill From',
        details: 'Invoice Details',
        draftDetails: 'Details',
        invoiceDate: 'Invoice Date',
        draftDate: 'Date',
        supplyDate: 'Supply Date',
        dueDate: 'Due Date',
        cancelledOn: 'Cancelled On',
        paymentTerms: 'Payment Terms',
        noteToCustomer: 'Note to Customer',
        selfBilling: 'Self-billing',
        vatNotes: 'VAT Notes',
        lines: {
            heading: 'Product or Service',
            description: 'Description',
            serviceCategory: 'Service Category',
            qty: 'Qty',
            unitPrice: 'Price',
            lineTotal: 'Subtotal',
            total: 'Total',
        },
        treatments: {
            exempt: 'Exempt',
            reverse_charge: 'Reverse charge',
            zero_rated: 'Zero-rated',
            non_reclaimable: 'VAT not reclaimable',
        },
        taxStatements: {
            reverse_charge: 'Reverse charge',
            non_reclaimable: 'Total includes VAT not reclaimed',
        },
        exemptionReasons: {
            art_132_1_i: 'VAT exempt pursuant to Article 132(1)(i) of the VAT Directive',
            art_132_1_n: 'VAT exempt pursuant to Article 132(1)(n) of the VAT Directive',
            art_132_1_j: 'VAT exempt pursuant to Article 132(1)(j) of the VAT Directive',
            export: 'Zero-rated export pursuant to Article 146 of the VAT Directive',
            intra_eu: 'Zero-rated Intra-Community supply pursuant to Article 138 of the VAT Directive',
            resale: 'Exempt — Resale certificate on file',
            nonprofit: 'Exempt — Nonprofit organization',
            government: 'Exempt — Government entity',
        },
        totals: {
            subtotal: 'Subtotal',
            total: 'Total',
            amountPaid: 'Amount Paid',
            amountDue: 'Amount Due',
        },
        payments: {
            heading: 'Payment History',
            entry: 'Payment #{{index}}',
        },
        statuses: {
            draft: 'Draft',
            sent: 'Sent',
            unpaid: 'Unpaid',
            partially_paid: 'Partially paid',
            paid: 'Paid',
            overdue: 'Overdue',
            cancelled: 'Cancelled',
            pending: 'Pending',
            approved: 'Approved',
            rejected: 'Rejected',
            on_hold: 'On hold',
        },
        thanks: 'Thank you for your business!',
    },
    report: {
        asOf: 'As of {{date}}',
        forPeriod: 'For the period {{start}} – {{end}}',
        amountsIn: 'All amounts in {{currency}}',
        managementLegend: 'Management report — unaudited',
        ledger: {
            unattributedCurrency: 'Currency not attributable',
            unattributedNote: 'Exchange-clearing lines facing more than one currency cannot be attributed to a single currency. They are shown apart, without a currency symbol.',
            filteredFunds: 'Filtered to {{count}} fund(s)',
            unallocatedOnly: 'Unallocated only — lines with no fund',
            fundTaggingDifference: 'Fund-tagging difference',
            fundTaggingNote: 'Under a fund filter a journal can lose some of its lines (the bank line carries no fund, the expense line does). The fund-tagging difference is that gap, shown so the columns foot.',
            account: 'Account',
            debit: 'Debit',
            credit: 'Credit',
            total: 'Total',
        },
        trialBalance: {
            title: 'Trial Balance',
            opening: 'Opening balance',
            movement: 'Movements',
            closing: 'Closing balance',
            categories: {
                assets: 'Assets',
                liabilities: 'Liabilities',
                equity: 'Equity',
                revenue: 'Revenue',
                expenses: 'Expenses',
            },
            priorResult: 'Result through {{date}} (before appropriation)',
            empty: 'No ledger activity for this period.',
            notBalancedTitle: 'This trial balance does not balance',
            notBalancedLine: '{{column}}: debit {{debit}}, credit {{credit}}',
            notBalancedBody: 'Total debits and total credits should be equal. Review the ledger for unbalanced journals.',
            fxClearingNote: 'Exchange-clearing accounts are listed here; the balance sheet leaves them out.',
            suspenseNote: 'Suspense accounts are listed here and on the balance sheet.',
            suspenseWarningTitle: 'Suspense balance of {{amount}}',
            suspenseWarningBody: 'This is uncategorised activity. Categorise it before relying on these figures.',
            workingDocumentNote: 'Revenue and expense accounts open at zero; their result before the period is carried on one equity line. A trial balance is a working document, not a statutory statement.',
        },
        cashFlow: {
            title: 'Cash Flow Statement (direct method)',
            activities: {
                operating: 'Cash flows from operating activities',
                investing: 'Cash flows from investing activities',
                financing: 'Cash flows from financing activities',
            },
            netActivities: {
                operating: 'Net cash from operating activities',
                investing: 'Net cash from investing activities',
                financing: 'Net cash from financing activities',
            },
            sections: {
                operating_customers: 'Receipts from customers and funders',
                operating_revenue: 'Other receipts from revenue',
                operating_suppliers: 'Payments to suppliers',
                operating_expenses: 'Expenses paid',
                operating_interest_received: 'Interest received',
                operating_interest_paid: 'Interest paid',
                operating_income_tax: 'Income taxes paid',
                operating_vat_taxes: 'VAT and other taxes',
                operating_payroll_other: 'Payroll and other current liabilities',
                operating_other_current_assets: 'Other current assets',
                operating_credit_cards: 'Credit card settlements',
                operating_deferred_revenue: 'Deferred revenue',
                operating_deposits_provisions: 'Deposits and provisions',
                investing: 'Purchases and sales of long-term assets',
                financing: 'Loans, equity and owner balances',
                outside_fx_transfers: 'Movements through the exchange clearing account (transfers between own currencies and cross-currency settlements)',
                cash: 'Other cash and bank accounts',
                outside_opening_balances: 'Opening balances posted',
                outside_suspense: 'Suspense (uncategorised)',
                unattributed: 'Currency not attributable',
                unmapped: 'Unclassified accounts',
                fund_tagging_difference: 'Cash movements of other funds and unallocated lines',
            },
            directions: {
                investing: { outflow: 'Purchases', inflow: 'Proceeds' },
                financing: { outflow: 'Repayments and other payments', inflow: 'Drawdowns and other receipts' },
            },
            otherSection: 'Other ({{key}})',
            netCashFromActivities: 'Net cash from activities',
            reconciling: 'Reconciling items outside the activities',
            totalReconciling: 'Total reconciling items',
            netChange: 'Net change in cash',
            openingCash: 'Cash at beginning of period',
            closingCash: 'Cash at end of period',
            openingCashAt: 'Cash at {{date}}',
            closingCashAt: 'Cash at {{date}}',
            empty: 'No cash accounts or cash movements for this period.',
            notReconciledTitle: 'Opening cash plus the net change does not equal closing cash',
            notReconciledPriorTitle: 'Prior period {{start}} – {{end}}: opening cash plus the net change does not equal closing cash',
            notReconciledBody: 'Review the ledger for journals that move cash without a balancing line.',
            comparativeUnavailableTitle: 'Comparative period {{start}} – {{end}} could not be retrieved',
            comparativeUnavailableBody: 'This statement shows the current period only. The figures shown are complete for that period.',
            notes: {
                cash: 'Cash is the balance of the bank and cash accounts (the Bank account type), including any overdrawn balance. A separate overdraft facility account is shown under financing activities.',
                nonCash: 'Transactions that do not move cash are left out.',
                vatAndCards: 'VAT or sales tax on a sale or purchase is shown with the operating item paid or received in the same transaction; when there is none (an asset, a loan, interest or income tax), it is shown under payments to suppliers or receipts from customers, so investing and financing are net of VAT. VAT and other taxes shows payments and refunds on VAT and sales-tax returns, and payments of other taxes. A card purchase is shown when the card is settled.',
                lookThrough: "A payment of an invoice is classified by the invoice's lines: the share for a fixed asset, an investment, a loan or other financing, interest or income tax is shown under that activity; the rest, VAT included, stays under receipts from customers or payments to suppliers. Credit-card settlements and sales-channel payouts are not split this way and stay on their own lines.",
                disposals: 'Gains and losses on the sale of an asset are shown under investing activities, so the proceeds of a sale are complete.',
                fxClearing: "A payment made from a bank account in another currency (for example a loan repaid from the USD account) shows under movements through the exchange clearing account in the paying currency, and under its activity in the account's currency.",
                fundTaggingDifference: "Opening and closing cash are the whole organisation's cash. Under a fund filter this line is the cash movement of all other funds and of lines with no fund, so the statement still reconciles; it is not a sign of mis-tagging. Tagging the bank line makes no difference.",
            },
        },
        openItems: {
            title: { ar: 'Debtor List', ap: 'Creditor List' },
            contact: { ar: 'Customer', ap: 'Supplier' },
            invoice: 'Invoice',
            invoiceDate: 'Invoice date',
            dueDate: 'Due date',
            daysOverdue: 'Days overdue',
            invoiceTotal: 'Invoice total',
            outstanding: 'Outstanding',
            noContact: 'No contact',
            contactTotal: 'Total {{name}}',
            agingTitle: 'Ageing',
            buckets: {
                current: 'Not yet due',
                '1-30': '1–30 days',
                '31-60': '31–60 days',
                '61-90': '61–90 days',
                '90+': 'Over 90 days',
            },
            total: 'Total',
            subtotals: {
                ar: {
                    debitBalances: 'Debit balances',
                    creditBalances: 'Credit balances (overpayments)',
                    net: 'Net receivable',
                },
                ap: {
                    debitBalances: 'Debit balances (overpayments)',
                    creditBalances: 'Credit balances',
                    net: 'Net payable',
                },
            },
            tieOut: {
                title: 'Reconciliation to the general ledger',
                account: 'Control account',
                listNet: 'Open items',
                openingBalances: 'Opening balances',
                manualJournals: 'Other journals',
                ledgerBalance: 'General ledger',
                unexplained: 'Unexplained',
                unexplainedTitle: 'Part of the ledger balance is unexplained',
                unexplainedBody: 'The open items, opening balances and other journals do not add up to the general-ledger balance. Review the control account.',
            },
            empty: 'No open items at {{date}}.',
            ledgerNote: 'Drawn from the general ledger: an invoice is open when the lines posted to it up to this date do not net to zero, whatever its status today. The dashboard ageing is a status-based management view and can differ.',
            editLimitNote: 'Editing an issued invoice re-posts it in place, so a list for a past date can change afterwards.',
        },
        balanceSheet: {
            title: 'Balance Sheet',
            assets: 'Assets',
            currentAssets: 'Current Assets',
            nonCurrentAssets: 'Non-Current Assets',
            totalCurrentAssets: 'Total Current Assets',
            totalNonCurrentAssets: 'Total Non-Current Assets',
            totalAssets: 'Total Assets',
            liabilitiesAndEquity: 'Liabilities & Equity',
            currentLiabilities: 'Current Liabilities',
            nonCurrentLiabilities: 'Non-Current Liabilities',
            totalCurrentLiabilities: 'Total Current Liabilities',
            totalNonCurrentLiabilities: 'Total Non-Current Liabilities',
            totalLiabilities: 'Total Liabilities',
            equity: 'Equity',
            totalEquity: 'Total Equity',
            totalLiabilitiesAndEquity: 'Total Liabilities & Equity',
            beforeAppropriation: 'Before appropriation of result',
            noCurrentAssets: 'No current assets',
            noNonCurrentAssets: 'No non-current assets',
            noCurrentLiabilities: 'No current liabilities',
            noNonCurrentLiabilities: 'No non-current liabilities',
            noEquity: 'No equity',
            noteTitle: 'Balance Sheet Note',
            noteBody: 'Current year net income is included in equity for balance sheet purposes. At year-end, revenue and expense accounts will be closed to retained earnings/net assets.',
            balanceCheck: 'Balance Check',
            notBalanced: 'Balance Sheet is NOT Balanced',
            notBalancedPrior: 'Comparative Balance Sheet as of {{date}} is NOT Balanced',
            liabilitiesPlusEquity: 'Liabilities + Equity',
            unexplainedDifference: 'Unexplained difference',
            notBalancedBody: 'This difference is not a currency translation adjustment — the underlying books are out of balance. Review recent journal entries and opening balances, or contact support.',
            missingRatesTitle: 'Exchange rates missing: {{currencies}}',
            missingRatesTitlePrior: 'Exchange rates missing as of {{date}}: {{currencies}}',
            missingRatesBody: 'Amounts in these currencies are shown untranslated (1:1). Translated totals and the Cumulative Translation Adjustment are unreliable until rates are available for the reporting date.',
            comparativeUnavailableTitle: 'Comparative figures as of {{date}} could not be retrieved for this report',
            comparativeUnavailableBody: 'The statement shows the current period only.',
        },
        translation: {
            title: 'Foreign Currency Translation Disclosures',
            subtitle: 'Per IFRS for SMEs Section 30.25-30.26',
            presentationCurrency: 'Presentation Currency',
            closingDate: 'Closing Date',
            method: 'Translation Method',
            methodClosingRate: 'Closing rate method (IFRS for SMEs 30.18)',
            functionalCurrencies: 'Functional Currencies Translated',
            ratesTitle: 'Closing Exchange Rates Applied',
            from: 'From',
            to: 'To',
            rate: 'Rate',
            rateDate: 'Rate Date',
            source: 'Source: European Central Bank (ECB)',
            cta: 'Cumulative Translation Adjustment',
            ctaNote: 'Translation differences recognized in equity per IFRS for SMEs 30.18(c)',
        },
        incomeStatement: {
            title: 'Income Statement',
            titleSuffixUnallocated: '— Unallocated Transactions',
            titleSuffixFundingSourceView: '— Funding Source View',
            titleSuffixFundingSources: '— {{count}} Funding Sources',
            revenue: 'Revenue',
            totalRevenue: 'Total Revenue',
            costOfSales: 'Cost of Sales',
            totalCostOfSales: 'Total Cost of Sales',
            grossProfit: 'Gross Profit',
            operatingExpenses: 'Operating Expenses',
            totalOperatingExpenses: 'Total Operating Expenses',
            employeeBenefits: 'Employee Benefits',
            depreciationAmortization: 'Depreciation & Amortization',
            rawMaterials: 'Raw Materials & Consumables',
            otherOperatingExpenses: 'Other Operating Expenses',
            operatingProfit: 'Operating Profit',
            financeCosts: 'Finance Costs',
            totalFinanceCosts: 'Total Finance Costs',
            profitBeforeTax: 'Profit Before Tax',
            taxExpense: 'Tax Expense',
            profitForPeriod: 'Profit for the Period',
            netProfitCaption: 'Net profit for the period',
            netLossCaption: 'Net loss for the period',
            directTransactions: 'Direct transactions',
            account: 'Account',
            totalColumn: 'Total',
            budget: 'Budget: {{amount}}',
            expenses: 'Expenses',
            totalExpenses: 'Total Expenses',
            comparisonNote: 'This Income Statement compares {{count}} projects for the period {{start}} to {{end}}.',
            periodNote: 'This Income Statement summarizes revenues and expenses for the period {{start}} to {{end}}.',
            ifrsNote: 'Prepared in accordance with IFRS for SMEs Section 5 (expenses classified by nature)',
            filteredProjects: 'Filtered by {{count}} project(s)',
            filteredFunds: 'Filtered by {{count}} funding source(s)',
            unallocatedOnly: 'Showing only unallocated transactions',
            translationNoteTitle: 'Currency Translation Note (IFRS SME Section 30)',
            translationNoteBody: 'Amounts translated to {{currency}} using closing rates as of {{date}}.',
            translationNoteBodyPrior: 'Comparative amounts translated to {{currency}} using closing rates as of {{date}}.',
            translationFunctionalCurrencies: 'Functional currencies: {{currencies}}',
            translationMissingRates: 'Note: Exchange rates not available for: {{currencies}}',
            translationMissingRatesPrior: 'Note: Exchange rates not available for the comparative period as of {{date}}: {{currencies}}',
            comparativeUnavailableTitle: 'Comparative figures for {{start}} — {{end}} could not be retrieved for this report',
            comparativeUnavailableBody: 'The statement shows the current period only.',
        },
        tax: {
            filingSupportLegend: 'Filing support report — unaudited',
            vatReturnTitle: '{{tax}} Return',
            salesTaxTitle: 'Sales Tax Report',
            salesTaxName: 'Sales Tax',
            outputSectionTitle: 'Output {{tax}} (Sales to Customers)',
            inputSectionTitle: 'Input {{tax}} (Purchases from Suppliers)',
            salesByRateTitle: 'Tax Collected by Rate',
            outputCard: 'Output {{tax}}',
            outputHint: 'Collected on sales',
            inputCard: 'Input {{tax}}',
            inputHint: 'Recoverable on purchases',
            netCard: 'Net {{tax}}',
            amountDueLabel: 'Amount due to tax authority',
            refundDueLabel: 'Refund due from tax authority',
            balancedLabel: 'Balanced — nothing to settle',
            totalOutput: 'Total Output {{tax}}',
            totalInput: 'Total Input {{tax}}',
            totalTaxCollected: 'Total Tax Collected',
            totalSales: 'Total Sales',
            taxCollected: 'Tax Collected',
            rateName: 'Rate',
            rateColumn: '{{tax}} Rate',
            rateColumnGeneric: 'Tax Rate',
            netAmount: 'Net Amount',
            grossAmount: 'Gross Amount',
            taxAmount: '{{tax}} Amount',
            taxAmountGeneric: 'Tax Amount',
            reverseChargeSuffix: '(reverse charge)',
            rateSubtotal: 'Subtotal {{rate}}',
            rateSubtotalExclReverseCharge: 'Subtotal {{rate}} (excl. reverse charge)',
            noEntries: 'No tax entries found for this period. Tax data will appear here once invoices or expenses with tax-split ledger entries are created.',
            noSalesEntries: 'No sales tax entries found for this period. Tax data will appear here once invoices with tax-split ledger entries are created.',
            summaryTitle: '{{tax}} Payment Summary',
            amountDue: 'Amount due to tax authority:',
            refundDue: 'Refund due from tax authority:',
            nilReturnAmount: 'Net amount:',
            taxCollectedToRemit: 'Tax collected to remit:',
            notOnReturnLabel: 'Other Sales Tax Payable activity (not on this return):',
            notOnReturnHint: 'Payments to the tax authority and lines without a sales rate. Shown so this report ties to the Sales Tax Payable account; not included in the tax to remit.',
            section: {
                sales_tax_payable: 'Sales and use tax',
                meals_rooms_tax_payable: 'Meals and rooms tax',
                other: 'Other tax',
            },
            sectionWithCode: '{{title}} ({{code}})',
            category: { sales: 'Sales', meals: 'Meals', rooms: 'Rooms', alcohol: 'Alcohol', other: 'Other' },
            categoryColumn: 'Category',
            sectionTotal: 'Section total',
            stateReturnTotal: 'Total tax on the state return',
            localOptionTitle: 'Local option tax by town',
            localOptionHint: 'Included in the state return above and reported there per town.',
            townColumn: 'Town',
            localTaxColumn: 'Local tax',
            localOptionTotal: 'Total local option tax',
            cityTitle: 'Paid to City of {{city}}',
            cityHint: 'Collected for the city and paid to it directly. Not on the state return.',
            cityTotal: 'Total paid to the city',
            ofWhichStateReturn: 'of which on the state return',
            ofWhichCity: 'of which paid to City of {{city}}',
            forPeriodEnding: 'For period ending {{end}}',
            complianceLine1: 'This report has been generated for tax compliance purposes.',
            complianceLine2: 'Please review all figures before submission to tax authorities.',
        },
        volunteer: {
            title: 'Volunteer Report',
            subtitle: '{{project}} · {{from}} – {{to}}',
            signupPage: 'Signup page: {{title}} · {{status}}',
            pageStatus: { draft: 'Draft', open: 'Open', closed: 'Closed' },
            noShifts: 'This project has no volunteer shifts yet.',
            summary: {
                title: 'Summary',
                people: 'Volunteers signed up',
                peopleHint: 'People, each counted once',
                filled: 'Places filled',
                filledHint: '{{pct}} of all places',
                open: 'Open places',
                openHint: 'Shifts in total: {{count}}',
                empty: 'Shifts with no one',
                emptyHint: 'Full shifts: {{count}}',
                confirmed: 'Confirmed',
                pending: 'Pending approval',
                waitlisted: 'Waitlisted',
                multiShift: 'Volunteers on more than one shift',
            },
            allRoles: 'All roles',
            allDays: 'All days',
            byRole: {
                title: 'By role',
                caption: 'Confirmed places against all places',
                role: 'Role',
                shifts: 'Shifts',
                full: 'Full',
                filled: 'Filled / places',
                fill: 'Fill',
                open: 'Open',
                waitlist: 'Waitlist',
            },
            byRoleAndShift: {
                title: 'By role and shift',
                caption: 'Every shift, in time order within its role',
                shiftsCount: 'Shifts: {{count}} · {{confirmed}} / {{capacity}} filled',
                day: 'Day',
                time: 'Time',
                shift: 'Shift',
                place: 'Place',
                filled: 'Filled / places',
                status: 'Status',
                full: 'Full',
                noOneYet: 'No one yet',
                nOpen: '{{count}} open',
                nOnWaitlist: '{{count}} on waitlist',
                nAwaitingApproval: '{{count}} pending approval',
                held: 'Held for approval',
                nextDay: '+1',
                nextDayNote: '+1 = ends next day',
            },
            byDay: {
                title: 'By day',
                caption: 'Shifts grouped by the date they start',
                day: 'Day',
                shifts: 'Shifts',
                volunteers: 'Volunteers',
                filled: 'Filled / places',
                fill: 'Fill',
                open: 'Open',
                lede: 'Volunteers per day counts each person once that day, even if they work two shifts.',
            },
            matrix: {
                title: 'Role × day',
                caption: 'Filled / places for each role on each day',
                role: 'Role',
                legend: {
                    empty: 'No one yet',
                    under: 'Under {{pct}}',
                    most: '{{pct}} or more',
                    full: 'Full',
                    none: 'No shifts that day',
                },
            },
            notes: {
                countingRule: 'Filled counts confirmed places only. Cancelled places are not counted. Waitlisted places and places pending approval are listed apart; a place pending approval is held, so it is not open.',
            },
        },
    },
};
//# sourceMappingURL=en.js.map